import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, LogOut, ChevronRight, ShoppingBag } from "lucide-react";
import { getMyOrders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyOrders().then(setOrders).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div data-testid="account-page" className="bg-bone pt-[72px] min-h-screen">
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full object-cover border border-line" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-sage flex items-center justify-center font-display text-xl text-forest">
                {user?.name?.[0] || user?.email?.[0] || "S"}
              </div>
            )}
            <div>
              <h1 className="font-display text-3xl md:text-4xl tracking-tight text-ink leading-tight">
                {user?.name || "Your account"}
              </h1>
              <p className="text-stone text-sm">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-sm font-semibold text-ink hover:bg-sand transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>

        <div className="mt-12">
          <h2 className="font-display text-2xl text-ink mb-6 flex items-center gap-2">
            <Package className="w-5 h-5 text-forest" strokeWidth={1.5} /> Order history
          </h2>

          {loading ? (
            <div className="space-y-4">
              {[0, 1].map((i) => <div key={i} className="h-24 rounded-xl bg-sand animate-pulse" />)}
            </div>
          ) : orders.length === 0 ? (
            <div className="rounded-xl border border-line bg-sand p-10 text-center">
              <div className="w-14 h-14 rounded-full bg-bone flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="w-6 h-6 text-stone" strokeWidth={1.25} />
              </div>
              <p className="text-stone">No orders yet. Your protocols will appear here.</p>
              <Link to="/shop" className="mt-5 inline-block rounded-full bg-forest text-[#14100F] px-6 py-3 text-sm font-semibold hover:bg-forest-dark transition-colors">
                Explore peptides
              </Link>
            </div>
          ) : (
            <div data-testid="order-history" className="space-y-4">
              {orders.map((o, idx) => (
                <motion.div
                  key={o.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                >
                  <Link
                    to={`/order/${o.id}`}
                    data-testid={`order-row-${o.id}`}
                    className="flex items-center justify-between gap-4 rounded-xl border border-line bg-sand hover:border-forest/30 hover:shadow-md p-5 transition-[box-shadow,border-color]"
                  >
                    <div>
                      <p className="font-mono text-xs text-stone">#{o.id.slice(0, 8).toUpperCase()}</p>
                      <p className="text-ink font-semibold mt-1">
                        {o.items.reduce((a, i) => a + i.quantity, 0)} item{o.items.reduce((a, i) => a + i.quantity, 0) > 1 ? "s" : ""} · {o.items.map((i) => i.name).join(", ")}
                      </p>
                      <p className="text-xs text-stone mt-1">
                        {new Date(o.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                        <span className="ml-2 inline-flex items-center rounded-full bg-sage text-forest px-2.5 py-0.5 text-[11px] font-semibold capitalize">{o.status}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-display text-xl text-ink">${o.total.toFixed(2)}</span>
                      <ChevronRight className="w-5 h-5 text-stone" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
